import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';

import {
  Firestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  collectionData,
  getDocs,
  addDoc,
  serverTimestamp,
  onSnapshot,
  limit,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';

import { ActivatedRoute, Router } from '@angular/router';
import {
  combineLatest,
  map,
  Observable,
  Subject,
  takeUntil,
  Subscription,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { AuthCheckService } from 'src/app/shared/services/auth-check.service';
import { ImageViewerDialogComponent } from 'src/app/shared/image-viewer-dialog/image-viewer-dialog.component';
import { ServiceOfferDialogComponent } from '../service-offer-dialog/service-offer-dialog.component';
import { CartService, CartItem } from 'src/app/shared/services/cart.service';
import { BookingLockService } from 'src/app/shared/services/booking-lock.service';
import { DemoVendorService } from 'src/app/shared/services/demo-vendor.service';

interface FieldConfig {
  id: string;
  fieldName: string;
  displayLabel: string;
  type: string;
  enabled: boolean;
  required: boolean;
  categoryId: string;
  priority: number;
  options?: string[] | string;
}

interface FieldOverride {
  id: string;
  fieldId: string;
  categoryId: string;
  enabled?: boolean;
  required?: boolean;
}

interface Review {
  name?: string;
  comment?: string;
}

@Component({
  selector: 'app-booking-detail',
  templateUrl: './booking-detail.component.html',
  styleUrls: ['./booking-detail.component.scss'],
})
export class BookingDetailComponent implements OnInit, OnDestroy {
  bookingAddress: string = ''; // ✅ ADDED
  showAllReviews: boolean = false;

  equipmentId!: string;
  equipmentData: any = null;
  categoryData: any = null;
  subcategoryData: any[] = [];
  fields: FieldConfig[] = [];
  averageRating: number = 0;
  totalReviews: number = 0;
  editingItemId: string | null = null;

  /* ==========================
     CUSTOM CALENDAR VARIABLES
     ========================== */
  showCalendarFrom = false;
  showCalendarTo = false;

  selectedStart: string | null = null;
  selectedEnd: string | null = null;

  startDate: Date | null = null;
  endDate: Date | null = null;

  currentDate: Date = new Date();
  currentMonth: number = this.currentDate.getMonth();
  currentYear: number = this.currentDate.getFullYear();
  questionLabelMap: any = {
    'How was the equipment condition?': 'Vehicle Condition',
    'How was the vendor service?': 'Vendor Service',
    'Was the delivery on time?': 'On-time Delivery',
  };

  weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // isLoggedIn: any;
  calendarDays: any[] = [];

  get currentMonthName(): string {
    return new Date(this.currentYear, this.currentMonth).toLocaleString(
      'default',
      { month: 'long' },
    );
  }

  /* ========================== */

  images: string[] = [];
  smallImages: (string | null)[] = [null, null, null, null];
  selectedImage!: string;
  readonly PREVIEW_IMAGE_URL = 'assets/no-image-available.jpg';

  isLoading = true;
  errorMessage = '';
  selectedPriceType: 'daily' | 'weekly' | 'monthly' | 'per_trip' | 'cbm' = 'daily';
  numberOfTrips: number = 1;
  volumeCbm: number = 1;
  canMinus: boolean = false;
  reviews: any[] = [];
  totalReviewsCount = 12;

  imagesLoadedCount = 0;

  onImageLoad() {
    this.imagesLoadedCount++;
  }

  private async waitForImages(
    targetCount: number,
    timeoutMs: number = 3000,
  ): Promise<void> {
    if (targetCount <= 0) return;

    return new Promise((resolve) => {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        if (this.imagesLoadedCount >= targetCount || elapsed >= timeoutMs) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }

  isDemo = false;

  private destroy$ = new Subject<void>();
  private dynamicFieldsSub?: Subscription;

  constructor(
    private firestore: Firestore,
    private route: ActivatedRoute,
    public router: Router,
    public ux: UxserviceService,
    private dialog: MatDialog,
    private authCheck: AuthCheckService,
    private cartService: CartService,
    private bookingLock: BookingLockService,
    private auth: Auth,
    private demoVendor: DemoVendorService,
  ) { }

  get isLoggedIn(): boolean {
    return !!this.auth.currentUser;
  }

  shareEquipment() {
    const url = window.location.href;
    const name = this.equipmentData?.name || 'Equipment';
    const title = name + ' Details';
    // const text = `Check out this ${name} on Aluguloo!`;

    if (navigator.share) {
      navigator
        .share({
          title: title,
          // text: text,
          url: url,
        })
        .then(() => {
          this.ux.showSuccess(`Booking link for "${name}" shared!`, 'Share');
        })
        .catch((error) => console.log('Error sharing', error));
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(url).then(
        () => {
          this.ux.showSuccess(
            `Booking link for "${name}" copied to clipboard!`,
            'Link Copied',
          );
        },
        (err) => {
          console.error('Could not copy text: ', err);
          this.ux.showError('Failed to copy link to clipboard.');
        },
      );
    }
  }

  openServiceOfferDialog() {
    this.router.navigate(['/register-business']);
  }

  /* ==========================
      LIFECYCLE
     ========================== */

  ngOnInit(): void {
    // Build initial month
    this.generateCalendar();

    this.route.paramMap.subscribe(async (params) => {
      const id = params.get('id');
      if (!id) {
        this.errorMessage = 'Invalid equipment id.';
        this.isLoading = false;
        return;
      }

      this.equipmentId = id;
      await this.loadEquipmentWithOverlay();
    });

    this.route.queryParamMap.subscribe((params) => {
      const editId = params.get('editCartItemId');
      if (editId) {
        this.editingItemId = editId;
        this.prefillEditData(editId);
      }
    });
  }

  private prefillEditData(editId: string) {
    const item = this.cartService.getCartItem(editId);
    if (item) {
      this.startDate = item.startDate ? new Date(item.startDate) : null;
      this.endDate = item.endDate ? new Date(item.endDate) : null;
      this.selectedPriceType = item.priceType as any;
      this.bookingAddress = item.address || '';
      if (item.priceType === 'cbm') {
        this.volumeCbm = item.daysCount || 1;
      } else if (item.priceType === 'per_trip') {
        this.numberOfTrips = item.daysCount || 1;
      }

      if (this.startDate) {
        this.selectedStart = this.startDate.toISOString().split('T')[0];
      }
      if (this.endDate) {
        this.selectedEnd = this.endDate.toISOString().split('T')[0];
      }

      this.generateCalendar();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.unsubscribeBookings) {
      this.unsubscribeBookings();
    }
  }

  /* ==========================
      CALENDAR CORE
     ========================== */
  openFromCalendar(event: Event) {
    this.showCalendarFrom = true;
    this.showCalendarTo = false;
    this.generateCalendar();
    event.stopPropagation();
  }

  openToCalendar(event: Event) {
    this.showCalendarTo = true;
    this.showCalendarFrom = false;
    this.generateCalendar();
    event.stopPropagation();
  }

  handleBigImageError() {
    this.selectedImage = this.PREVIEW_IMAGE_URL;
  }

  handleSmallImageError(index: number) {
    this.smallImages[index] = this.PREVIEW_IMAGE_URL;
  }
  generateCalendar() {
    let days = [];
    let firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
    let lastDate = new Date(
      this.currentYear,
      this.currentMonth + 1,
      0,
    ).getDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0); // normalize midnight

    // Check overlaps function
    const isDateBooked = (d: Date) => {
      const t = d.getTime();
      return this.bookedIntervals.some(
        (interval) => t >= interval.start && t <= interval.end,
      );
    };

    for (let i = 0; i < firstDay; i++) {
      days.push({ date: '', inactive: true, isToday: false, isPast: false });
    }

    for (let d = 1; d <= lastDate; d++) {
      const thisDate = new Date(this.currentYear, this.currentMonth, d);
      thisDate.setHours(0, 0, 0, 0);

      const isPast = thisDate < today;
      const isBooked = isDateBooked(thisDate);

      // Check if matches start or end
      const isStart = this.startDate
        ? thisDate.getTime() === this.startDate.getTime()
        : false;
      const isEnd = this.endDate
        ? thisDate.getTime() === this.endDate.getTime()
        : false;

      days.push({
        date: d,
        inactive: false,
        isToday: thisDate.getTime() === today.getTime(),
        isPast: isPast,
        isBooked: isBooked,
        isStart: isStart,
        isEnd: isEnd,
      });
    }

    this.calendarDays = days;
  }

  prevMonth() {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.generateCalendar();
  }

  nextMonth() {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateCalendar();
  }

  private formatDisplayDate(date: Date): string {
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  }

  selectStartDay(day: any) {
    if (day.inactive || !day.date || day.isBooked) return;

    const selected = new Date(this.currentYear, this.currentMonth, day.date);

    this.startDate = selected;
    this.selectedStart = this.formatDisplayDate(selected);

    // Auto-fill ONLY for weekly / monthly
    const e = new Date(selected);

    if (this.selectedPriceType === 'weekly') {
      e.setDate(e.getDate() + 7);
      this.endDate = e;
      this.selectedEnd = this.formatDisplayDate(e);
    } else if (this.selectedPriceType === 'monthly') {
      e.setDate(e.getDate() + 30);
      this.endDate = e;
      this.selectedEnd = this.formatDisplayDate(e);
    } else if (this.selectedPriceType === 'per_trip') {
      this.endDate = selected;
      this.selectedEnd = this.formatDisplayDate(selected);
    } else {
      // DAILY: user must pick end date manually
      this.endDate = null;
      this.selectedEnd = null;
    }

    this.showCalendarFrom = false;
    // Regenerate to show selection
    this.generateCalendar();
  }

  selectEndDay(day: any) {
    if (day.inactive || !day.date || day.isBooked) return;

    const selected = new Date(this.currentYear, this.currentMonth, day.date);

    // If daily AND start date is not selected → auto-fill start date
    if (this.selectedPriceType === 'daily' && !this.startDate) {
      this.startDate = selected;
      this.selectedStart = this.formatDisplayDate(selected);
    }

    // VALIDATION: Prevent same day for daily
    if (this.selectedPriceType === 'daily' && this.startDate) {
      if (selected.getTime() === this.startDate.getTime()) {
        this.ux.showError(
          'End date cannot be the same as start date for daily bookings.',
        );
        return;
      }
    }

    // Set end date
    this.endDate = selected;
    this.selectedEnd = this.formatDisplayDate(selected);

    // Enable minus button only if end date > start date
    if (this.startDate && this.endDate > this.startDate) {
      this.canMinus = true;
    } else {
      this.canMinus = false;
    }

    this.showCalendarTo = false;
    // Regenerate to show selection
    this.generateCalendar();
  }

  @HostListener('document:click')
  closeCalendars() {
    this.showCalendarFrom = false;
    this.showCalendarTo = false;
  }

  /* ==========================
      PRICE LOGIC
     ========================== */

  onPlusClick() {
    if (!this.startDate) return;

    // If end date is not yet selected → start from start date
    if (!this.endDate) {
      this.endDate = new Date(this.startDate);
    }

    const e = new Date(this.endDate);

    if (this.selectedPriceType === 'weekly') {
      e.setDate(e.getDate() + 7);
    } else if (this.selectedPriceType === 'monthly' || this.selectedPriceType === 'cbm') {
      e.setDate(e.getDate() + 30);
    } else {
      // DAILY
      e.setDate(e.getDate() + 1);
    }

    this.endDate = e;
    this.selectedEnd = this.formatDisplayDate(e);
    this.canMinus = true;
  }

  onMinusClick() {
    if (!this.startDate || !this.endDate) return;

    const e = new Date(this.endDate);
    const s = new Date(this.startDate);

    if (this.selectedPriceType === 'weekly') {
      e.setDate(e.getDate() - 7);
    } else if (this.selectedPriceType === 'monthly' || this.selectedPriceType === 'cbm') {
      e.setDate(e.getDate() - 30);
    } else {
      // DAILY
      e.setDate(e.getDate() - 1);
    }

    // Prevent going below start date
    if (e <= s) {
      this.canMinus = false;
      return;
    }

    this.endDate = e;
    this.selectedEnd = this.formatDisplayDate(e);

    // Disable when reaching start date
    if (e <= s) {
      this.canMinus = false;
    } else {
      this.canMinus = true;
    }
  }

  onPriceTypeChange() {
    this.canMinus = false;
    this.numberOfTrips = 1; // reset
    this.volumeCbm = 1; // reset

    if (!this.startDate) return;

    const s = new Date(this.startDate);
    const e = new Date(s);

    if (this.selectedPriceType === 'weekly') {
      e.setDate(e.getDate() + 7);
      this.endDate = e;
      this.selectedEnd = this.formatDisplayDate(e);
    } else if (this.selectedPriceType === 'monthly' || this.selectedPriceType === 'cbm') {
      e.setDate(e.getDate() + 30);
      this.endDate = e;
      this.selectedEnd = this.formatDisplayDate(e);
    } else if (this.selectedPriceType === 'per_trip') {
      this.endDate = s;
      this.selectedEnd = this.formatDisplayDate(s);
    } else {
      // DAILY & CBM — user chooses manually
      this.endDate = null;
      this.selectedEnd = null;
    }
  }

  get daysCount(): number {
    if (!this.startDate || !this.endDate) return 1;

    const s = new Date(this.startDate);
    const e = new Date(this.endDate);

    const diff = e.getTime() - s.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    return days > 0 ? days : 0;
  }

  get weeksCount(): number {
    return this.daysCount > 0 ? Math.ceil(this.daysCount / 7) : 0;
  }

  get monthsCount(): number {
    return this.daysCount > 0 ? Math.ceil(this.daysCount / 30) : 0;
  }

  get computedTotal(): number {
    if (!this.equipmentData) return 0;

    const daily = Number(this.equipmentData.dailyRate || 0);
    const weekly = Number(this.equipmentData.weeklyRate || 0);
    const monthly = Number(this.equipmentData.monthlyRate || 0);

    const days = this.daysCount;

    if (this.selectedPriceType === 'daily') {
      if (days <= 0) return 0;
      return days * daily;
    }
    if (this.selectedPriceType === 'weekly') {
      if (days <= 0) return 0;
      return this.weeksCount * weekly;
    }
    if (this.selectedPriceType === 'monthly') {
      if (days <= 0) return 0;
      return this.monthsCount * monthly;
    }
    if (this.selectedPriceType === 'per_trip') {
      const tripRate = Number(this.equipmentData.tripRate || 0);
      return this.numberOfTrips * tripRate;
    }
    if (this.selectedPriceType === 'cbm') {
      if (days <= 0) return 0;
      const cbmRate = Number(this.equipmentData.perCubicMetreRate || 0);
      return days * cbmRate;
    }

    return 0;
  }

  /* ==========================
      FIRESTORE FETCHING
     ========================== */

  private async loadEquipmentWithOverlay(): Promise<void> {
    this.isLoading = true;
    await this.ux.showOverlay('Loading equipment details...');
    try {
      // Parallelize fetching
      await Promise.all([
        this.fetchEquipmentDetails(),
        this.fetchExistingBookings(),
      ]);
      // Load reviews in background (fire and forget)
      this.loadReviews(this.equipmentId);
      // Overlay closes as soon as data is ready — images stream in naturally
    } catch (err) {
      console.error('Error loading equipment:', err);
      this.errorMessage = 'Failed to load equipment details.';
    } finally {
      this.isLoading = false;
      this.ux.closeOverlay();
    }
  }

  private setupImagesFromData() {
    this.images = Array.isArray(this.equipmentData?.images)
      ? this.equipmentData.images
      : [];

    // Always update selectedImage to the first available image of the new equipment
    this.selectedImage =
      this.images.length > 0 ? this.images[0] : this.PREVIEW_IMAGE_URL;

    const remaining = this.images.slice(1, 5);
    this.smallImages = [
      remaining[0] ?? null,
      remaining[1] ?? null,
      remaining[2] ?? null,
      remaining[3] ?? null,
    ];
  }

  private async fetchEquipmentDetails(): Promise<void> {
    try {
      this.isLoading = true;

      const equipmentRef = doc(
        this.firestore,
        `equipment-list/${this.equipmentId}`,
      );

      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        this.errorMessage = 'Equipment not found.';
        this.isLoading = false;
        return;
      }

      this.equipmentData = {
        id: equipmentSnap.id,
        ...equipmentSnap.data(),
      };

      // Flag demo equipment
      this.isDemo = await this.demoVendor.isDemoEquipmentAsync(this.equipmentData);

      // Automatically select first available rate type
      if (this.equipmentData.hasRentalRates !== false) {
        const getHasRate = (field: string, valKey: string) => {
          if (this.equipmentData[field] !== undefined) {
            return this.equipmentData[field];
          }
          const num = Number(this.equipmentData[valKey]);
          return !isNaN(num) && num > 0;
        };

        if (getHasRate('hasDailyRate', 'dailyRate')) {
          this.selectedPriceType = 'daily';
        } else if (getHasRate('hasWeeklyRate', 'weeklyRate')) {
          this.selectedPriceType = 'weekly';
        } else if (getHasRate('hasMonthlyRate', 'monthlyRate')) {
          this.selectedPriceType = 'monthly';
        } else if (getHasRate('hasTripRate', 'tripRate')) {
          this.selectedPriceType = 'per_trip';
        } else if (getHasRate('hasPerCubicMetreRate', 'perCubicMetreRate')) {
          this.selectedPriceType = 'cbm';
        }
      }

      // Dependencies that need equipmentData
      this.setupImagesFromData();

      const promises: Promise<any>[] = [];

      if (this.equipmentData.categoryId) {
        const cRef = doc(
          this.firestore,
          `vehicle-categories/${this.equipmentData.categoryId}`,
        );
        promises.push(
          getDoc(cRef).then((cSnap) => {
            this.categoryData = cSnap.exists() ? cSnap.data() : null;
          }),
        );
      }

      const catOrSub =
        this.equipmentData.subcategoryId || this.equipmentData.categoryId || '';

      promises.push(this.loadDynamicFields(catOrSub));
      promises.push(this.loadRecommended(this.equipmentData.emirate || ''));

      await Promise.all(promises);

      this.isLoading = false;
    } catch (err) {
      console.error(err);
      this.errorMessage = 'Failed to load equipment details.';
      this.isLoading = false;
    }
  }

  private loadDynamicFields(categoryOrSubId: string): Promise<void> {
    return new Promise((resolve) => {
      // 1. Clear previous subscription if any (to prevent leaks on param change)
      if (this.dynamicFieldsSub) {
        this.dynamicFieldsSub.unsubscribe();
      }

      try {
        const fieldRef = collection(this.firestore, 'fieldConfig');
        const overrideRef = collection(this.firestore, 'fieldOverrides');

        const fQ = query(
          fieldRef,
          where('categoryId', 'in', ['all', categoryOrSubId]),
        );
        const oQ = query(
          overrideRef,
          where('categoryId', '==', categoryOrSubId),
        );

        const fields$ = collectionData(fQ, { idField: 'id' }) as Observable<
          FieldConfig[]
        >;

        const overrides$ = collectionData(oQ, { idField: 'id' }) as Observable<
          FieldOverride[]
        >;

        this.dynamicFieldsSub = combineLatest([fields$, overrides$])
          .pipe(
            map(([fields, overrides]) =>
              fields
                .map((field) => {
                  const o = overrides.find((x) => x.fieldId === field.id);

                  return {
                    ...field,
                    enabled: o?.enabled ?? field.enabled,
                    required: o?.required ?? field.required,
                    options: Array.isArray(field.options)
                      ? field.options
                      : field.options
                        ? (field.options as string)
                          .split(',')
                          .map((v) => v.trim())
                        : [],
                  };
                })
                .filter((f) => f.enabled)
                .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
            ),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (fields) => {
              // Filter out Plate Number explicitly
              this.fields = fields.filter(
                (f) =>
                  f.fieldName !== 'plateNumber' &&
                  f.displayLabel !== 'Plate Number',
              );
              resolve(); // Resolve expectation on first load
            },
            error: () => {
              this.fields = [];
              resolve(); // Resolve even on error to unblock loading
            },
          });
      } catch {
        this.fields = [];
        resolve();
      }
    });
  }

  private async loadRecommended(emirate: string): Promise<void> {
    try {
      if (!emirate) {
        this.subcategoryData = [];
        return;
      }

      const ref = collection(this.firestore, 'equipment-list');
      // Only fetch paid-activated listings for the recommended section
      const q = query(
        ref,
        where('emirate', '==', emirate),
        where('status', '==', 'Active'),
        where('isMarketplaceActive', '==', true),
        limit(10), // fetch extra to allow for expiry filtering
      );

      const snap = await getDocs(q);
      const now = new Date();
      const list: any[] = [];

      snap.forEach((d: any) => {
        if (d.id === this.equipmentId) return; // skip current equipment

        const data = d.data() || {};

        list.push({ id: d.id, ...data });
      });

      this.subcategoryData = list.slice(0, 5);
    } catch {
      this.subcategoryData = [];
    }
  }

  /* ==========================
     AVAILABILITY LOGIC
     ========================== */
  bookedIntervals: { start: number; end: number }[] = [];

  private unsubscribeBookings?: () => void;

  private fetchExistingBookings(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.equipmentId) {
        resolve();
        return;
      }

      try {
        const bookingsRef = collection(this.firestore, 'booking-list');
        // Query bookings for this equipment that are NOT cancelled
        const validStatuses = [
          'Pending',
          'Confirmed',
          'Paid',
          'Completed',
          'Accepted',
          'Rejected',
        ];

        const qSingle = query(
          bookingsRef,
          where('equipmentId', '==', this.equipmentId),
          where('status', 'in', validStatuses),
        );

        const qArray = query(
          bookingsRef,
          where('equipmentIds', 'array-contains', this.equipmentId),
          where('status', 'in', validStatuses),
        );

        // Unsubscribe previous listener if exists
        if (this.unsubscribeBookings) {
          this.unsubscribeBookings();
        }

        // Real-time listeners handler
        const processSnapshots = async (snaps: any[]) => {
          const intervals: { start: number; end: number }[] = [];

          // Helper to normalize to midnight
          const toMid = (d: Date) => {
            const n = new Date(d);
            n.setHours(0, 0, 0, 0);
            return n.getTime();
          };

          const rejectedRanges: {
            start: number;
            end: number;
            customerId: string;
          }[] = [];
          const firestoreIntervals: { start: number; end: number }[] = [];
          const processedDocIds = new Set<string>();

          // 1. Add Firestore Bookings
          snaps.forEach((snap) => {
            snap.forEach((docSnap: any) => {
              if (processedDocIds.has(docSnap.id)) return;
              processedDocIds.add(docSnap.id);

              const data = docSnap.data();
              const cust = data['customer'] || {};
              const status = (data['status'] || '').toLowerCase().trim();
              const customerId = data['customerId'] || cust['id'] || ''; // robust fetch

              // Check multi-equipment array if present
              if (Array.isArray(data['equipments']) && data['equipments'].length > 0) {
                data['equipments'].forEach((eqItem: any) => {
                  if (eqItem.equipmentId === this.equipmentId) {
                    const startRaw = eqItem.startDate;
                    const endRaw = eqItem.endDate;
                    if (startRaw && endRaw) {
                      const sDate = startRaw.toDate ? startRaw.toDate() : new Date(startRaw);
                      const eDate = endRaw.toDate ? endRaw.toDate() : new Date(endRaw);
                      const sMid = toMid(sDate);
                      const eMid = toMid(eDate);

                      if (status !== 'rejected') {
                        intervals.push({ start: sMid, end: eMid });
                        firestoreIntervals.push({ start: sMid, end: eMid });
                      } else {
                        rejectedRanges.push({ start: sMid, end: eMid, customerId });
                      }
                    }
                  }
                });
              }

              // Fallback / root level dates
              const startRaw = data['startDate'] || cust['startDate'];
              const endRaw = data['endDate'] || cust['endDate'];

              if (startRaw && endRaw && data['equipmentId'] === this.equipmentId) {
                const sDate = startRaw.toDate
                  ? startRaw.toDate()
                  : new Date(startRaw);
                const eDate = endRaw.toDate
                  ? endRaw.toDate()
                  : new Date(endRaw);

                const sMid = toMid(sDate);
                const eMid = toMid(eDate);

                let shouldBlock = true;

                // Special logic for Rejected:
                // Make it always available (unblock) if status is Rejected.
                if (status === 'rejected') {
                  shouldBlock = false;
                  // Store this range to filter out corresponding lock later
                  rejectedRanges.push({ start: sMid, end: eMid, customerId });
                }

                if (shouldBlock) {
                  // Avoid duplicate interval if already added from equipments array
                  const alreadyAdded = intervals.some(inv => inv.start === sMid && inv.end === eMid);
                  if (!alreadyAdded) {
                    intervals.push({ start: sMid, end: eMid });
                    firestoreIntervals.push({ start: sMid, end: eMid });
                  }
                }
              }
            });
          });

          // 2. Add Real-time Locks (Re-fetch on every update to safe)
          try {
            const locks = await this.bookingLock.getLockedDates(
              this.equipmentId,
            );
            locks.forEach((lock) => {
              const s = new Date(lock.start);
              const e = new Date(lock.end);
              const sMid = toMid(s);
              const eMid = toMid(e);

              const rejectedMatch = rejectedRanges.find(
                (r) => r.start === sMid && r.end === eMid,
              );

              if (!rejectedMatch) {
                intervals.push({ start: sMid, end: eMid });
              } else {
                console.log(
                  'Self-healing: Removing ghost lock for rejected booking',
                );
                this.bookingLock
                  .unlockVehicle(
                    this.equipmentId,
                    s,
                    e,
                    rejectedMatch.customerId,
                  )
                  .catch((err) => console.error('Self-healing failed', err));
              }
            });
          } catch (err) {
            console.error('Error fetching locks:', err);
          }

          this.bookedIntervals = intervals;
          this.generateCalendar();
          resolve();
        };

        let snapSingle: any = null;
        let snapArray: any = null;

        const unsub1 = onSnapshot(qSingle, (s1) => {
          snapSingle = s1;
          processSnapshots([snapSingle, snapArray].filter(Boolean));
        }, (error) => {
          console.error('Error in single booking listener:', error);
          resolve();
        });

        const unsub2 = onSnapshot(qArray, (s2) => {
          snapArray = s2;
          processSnapshots([snapSingle, snapArray].filter(Boolean));
        }, (error) => {
          console.error('Error in array booking listener:', error);
          resolve();
        });

        this.unsubscribeBookings = () => {
          unsub1();
          unsub2();
        };
      } catch (error) {
        console.error('Error setting up booking listener:', error);
        resolve();
      }
    });
  }

  get isSelectionUnavailable(): boolean {
    if (!this.startDate) return false;

    // effective end date
    let effectiveEnd = this.endDate;
    if (!effectiveEnd) {
      if (this.selectedPriceType === 'per_trip') {
        effectiveEnd = this.startDate; // Overlap check for single day/point
      } else {
        return false; // Incomplete selection
      }
    }

    const s = this.startDate.getTime();
    const e = effectiveEnd.getTime();

    // Check overlap with any booked interval
    // Overlap if (StartA <= EndB) and (EndA >= StartB)
    return this.bookedIntervals.some((interval) => {
      return s <= interval.end && e >= interval.start; // Inclusive overlap
    });
  }

  async onProceed() {
    if (!this.equipmentData) return;

    // 1. Validation First
    if (
      !this.startDate ||
      (!this.endDate && this.selectedPriceType !== 'per_trip')
    ) {
      this.ux.showError('Please select both dates');
      return;
    }

    if (!this.bookingAddress || !this.bookingAddress.trim()) {
      this.ux.showError('Please enter a location');
      return;
    }

    // Check availability
    if (this.isSelectionUnavailable) {
      this.ux.showError(
        'This vehicle is already booked for the selected dates. Please choose different dates.',
      );
      return;
    }

    // 2. Build the Cart Item
    const item: CartItem = {
      equipmentId: this.equipmentId,
      equipmentData: this.equipmentData,
      startDate: this.startDate.toISOString(),
      endDate: this.endDate
        ? this.endDate.toISOString()
        : this.startDate.toISOString(),
      priceType: this.selectedPriceType,
      rate:
        this.selectedPriceType === 'daily'
          ? this.equipmentData.dailyRate
          : this.selectedPriceType === 'weekly'
            ? this.equipmentData.weeklyRate
            : this.selectedPriceType === 'monthly'
              ? this.equipmentData.monthlyRate
              : this.selectedPriceType === 'per_trip'
                ? this.equipmentData.tripRate
                : this.equipmentData.perCubicMetreRate,
      totalAmount: this.computedTotal,
      daysCount: this.selectedPriceType === 'per_trip' ? this.numberOfTrips : this.daysCount,
      vendorId: this.equipmentData.bywhom,
      address: this.bookingAddress,
    };

    // 3. Flow Check
    // Both Guests and Authenticated users can Add to Cart.
    // Login and Role setup are checked when "Confirm Enquiry" is clicked in the Cart page.
    if (this.editingItemId) {
      if (this.cartService.updateCartItem(this.editingItemId, item)) {
        this.ux.showSuccess('Cart item updated successfully!');
        this.router.navigate(['/cart']);
      }
    } else {
      if (this.cartService.addToCart(item)) {
        this.ux.showSuccess('Item added to cart successfully!');

        // Clear local validation states/fields on success
        this.startDate = null;
        this.endDate = null;
        this.selectedStart = null;
        this.selectedEnd = null;
        this.bookingAddress = '';
        this.canMinus = false;
        this.generateCalendar();
      }
    }
  }

  async loadReviews(equipmentId: string) {
    const revRef = collection(this.firestore, `reviews/${equipmentId}/items`);
    // Limit reviews fetched
    const q = query(revRef, limit(10));
    const snap = await getDocs(q);

    this.reviews = [];
    let ratingSum = 0;

    // Parallelize User Fetching
    const reviewPromises = snap.docs.map(async (docSnap) => {
      const data: any = docSnap.data();
      const userId = data.userId;
      let userData: any = null;

      ratingSum += data.rating;

      if (userId) {
        const userRef = doc(this.firestore, `users-list/${userId}`);
        const userSnap = await getDoc(userRef);
        userData = userSnap.exists() ? userSnap.data() : null;
      }

      const finalReviewText: string = data.finalReview || '';

      return {
        id: docSnap.id,
        rating: data.rating,
        finalReview: finalReviewText,
        questions: (data.questions || []).map((q: any) => ({
          question: this.questionLabelMap[q.question] || q.question,
          answer: q.answer,
        })),
        userId,
        userName: userData?.firstname || 'User',
        profilePic: userData?.profilePicUrl || null,
        date: data.createdAt?.toDate().toDateString() || '',
        expanded: false,
        needsMore: finalReviewText.length > 150,
      };
    });

    this.reviews = await Promise.all(reviewPromises);

    this.totalReviews = this.reviews.length;
    this.averageRating =
      this.totalReviews > 0
        ? Number((ratingSum / this.totalReviews).toFixed(1))
        : 0;
  }

  isIncluded(type: string): boolean {
    if (!this.equipmentData) return false;

    const typeFieldMap: { [key: string]: string } = {
      daily: 'dailyWith',
      weekly: 'weeklyWith',
      monthly: 'monthlyWith',
      per_trip: 'tripWith',
    };

    const prefix = typeFieldMap[this.selectedPriceType] || 'dailyWith';
    const fieldName = prefix + type;
    const value = this.equipmentData[fieldName];

    if (value !== undefined && value !== null) {
      return value === 'yes';
    }

    // Fallback logic for legacy data if needed
    if (type === 'Driver') {
      const legacyDriver = this.equipmentData.withDriver;
      return (
        legacyDriver === 'Yes' ||
        legacyDriver === 'yes' ||
        legacyDriver === true
      );
    }
    if (type === 'Fuel') {
      const legacyFuel = this.equipmentData.withFuel;
      return (
        legacyFuel === 'Yes' || legacyFuel === 'yes' || legacyFuel === true
      );
    }

    return false;
  }

  reportListing() {
    console.warn('Report listing: not implemented yet.');
  }

  showMore(r: any) {
    this.ux.showSuccess('Show more coming soon');
  }

  showAll() {
    this.ux.showSuccess('Show all reviews coming soon');
  }

  viewAll() {
    const emirate = this.equipmentData?.emirate;
    if (emirate) {
      this.router.navigate(['/category', 'all'], { queryParams: { emirate } });
    } else {
      this.router.navigate(['/category', 'all']);
    }
  }

  openImageViewer(index: number) {
    const images = [
      this.selectedImage || this.PREVIEW_IMAGE_URL,
      ...this.smallImages.filter((i) => !!i),
    ];

    this.dialog.open(ImageViewerDialogComponent, {
      width: '900px',
      height: '600px',
      panelClass: 'image-viewer-dialog',
      data: { images, index },
    });
  }
  get plusTooltip(): string {
    if (this.selectedPriceType === 'daily') return '+1 day';
    if (this.selectedPriceType === 'weekly') return '+7 days';
    if (this.selectedPriceType === 'monthly' || this.selectedPriceType === 'cbm') return '+30 days';
    return '+1 day';
  }

  get minusTooltip(): string {
    if (this.selectedPriceType === 'daily') return '-1 day';
    if (this.selectedPriceType === 'weekly') return '-7 days';
    if (this.selectedPriceType === 'monthly' || this.selectedPriceType === 'cbm') return '-30 days';
    return '-1 day';
  }
}
